/**
 * Solid single-color team-color swatch, used everywhere a team needs
 * at-a-glance visual identity without a licensed logo (MLB/NBA
 * playoff tiles). Pass `sport="mlb"` or `sport="nba"` to auto-resolve
 * the real brand color pair; pass `colors={[a, b]}` directly to skip
 * the lookup. iter87.260 — reverted from two-tone diagonal-split to a
 * single solid primary color per operator directive.
 * iter87.316 (user-requested, P1) — added `sport="nfl"` and
 * `sport="epl"`/`"laliga"`/`"soccer"` (interchangeable — a club can
 * show up under a Champions League/generic-club template without the
 * caller knowing which specific league it's from, so all three check
 * both EPL and La Liga maps together) so this component is ready for
 * soccer/NFL use anywhere, not just MLB/NBA.
 * iter87.511 (user-reported — "Real Madrid/Stuttgart white-on-white
 * is invisible, Aston Villa's light blue is hard to read") — initials
 * now flip to black automatically on light brand colors (luminance
 * check below), white everywhere else. Shared by every league's color
 * map, so this one fix sweeps MLB/NBA/NFL/EPL/La Liga/NWSL/MLS/UCL.
 */
import { mlbTeamColorPair } from "@/lib/mlbTeamColors";
import { nbaTeamColorPair } from "@/lib/nbaTeamColors";
import { nflTeamColorPair } from "@/lib/nflTeamColors";
import { eplTeamColorPair } from "@/lib/eplTeamColors";
import { LA_LIGA_TEAM_COLORS } from "@/lib/laLigaTeamColors";
import { nwslTeamColorPair } from "@/lib/nwslTeamColors";
import { mlsTeamColorPair } from "@/lib/mlsTeamColors";
import { uclTeamColorPair } from "@/lib/uclTeamColors";

function soccerTeamColorPair(name) {
    return LA_LIGA_TEAM_COLORS[name] || eplTeamColorPair(name);
}

// Perceived-brightness (ITU-R BT.601) luminance, 0-1. Light brand
// colors (white, pale yellow, pale blue/pink) need black initials;
// everything darker keeps the existing white.
export function readableTextColor(hex) {
    const c = (hex || "").replace("#", "");
    if (c.length !== 6) return "#ffffff";
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? "#000000" : "#ffffff";
}

export function initialsFor(name) {
    const words = (name || "").trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "?";
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return words.map((w) => w[0]).join("").slice(0, 3).toUpperCase();
}

export function TeamSwatch({ name, sport, colors, size = "w-8 h-8 text-xs", testId }) {
    const resolved = colors
        || (sport === "nba" ? nbaTeamColorPair(name)
            : sport === "mlb" ? mlbTeamColorPair(name)
            : sport === "nfl" ? nflTeamColorPair(name)
            : sport === "nwsl" ? nwslTeamColorPair(name)
            : sport === "mls" ? mlsTeamColorPair(name)
            : sport === "ucl" ? uclTeamColorPair(name)
            : (sport === "epl" || sport === "laliga" || sport === "soccer") ? soccerTeamColorPair(name)
            : null)
        || ["#3f3f46", "#3f3f46"];
    const [primary] = resolved;
    return (
        <span
            className={`${size} shrink-0 rounded-full border-2 border-white/20 shadow-[2px_2px_0px_#000] flex items-center justify-center font-black tracking-tighter leading-none drop-shadow-md`}
            style={{ background: primary, color: readableTextColor(primary) }}
            data-testid={testId || "team-swatch"}
        >
            {initialsFor(name)}
        </span>
    );
}
